import React, { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { formatDistanceToNowStrict } from 'date-fns';
import { HubConnectionBuilder } from '@microsoft/signalr';
import helpers from '../utils/helpers';
import chatService from '../services/chatService';
import notificationservice from '../services/notificationservice';
import tr from 'date-fns/locale/tr';

const ChatContext = createContext();

const ChatProvider = (props) => {
  const { children } = props;
  const [threads, setThreads] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [scrollCount, setScrollCount] = useState(0);
  const [isFinish, setIsFinish] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [unReadedNotificationCount, setUnReadedNotificationCount] = useState({
    seller: 0,
    buyer: 0,
    both: 0,
    total: 0
  });
  const [lastBalance, setLastBalance] = useState(null);
  const [unReadedMessageCount, setUnReadedMessageCount] = useState({});

  const [threadModel, setThreadModel] = useState({});
  const [newMessage, setNewMessage] = useState(null);
  const [newNotification, setNewNotification] = useState(null);
  const [newReadedMessage, setNewReadedMessage] = useState([]);
  const [newChat, setNewChat] = useState(null);
  const [announcement, setAnnouncement] = useState(null);

  const getMessages = () => {
    const model = {
      threadId: activeThreadId,
      scrollCount,
    };
    setIsLoading(true);
    chatService.getChatMessages(model).then((response) => {
      if (response) {
        if (Array.isArray(response) && response.length === 0) {
          setIsFinish(true);
          return;
        }
        const allThreads = { ...threadModel };
        if (!allThreads[activeThreadId]) {
          allThreads[activeThreadId] = {};
        }
        const currentThread = allThreads[activeThreadId];
        if (!currentThread.messages) {
          currentThread.messages = [];
        }
        currentThread.messages.unshift(...response);

        allThreads[activeThreadId] = currentThread;

        setThreadModel(allThreads);
        setIsLoading(false);
      }
    });
  };

  const getUnReadedNotficationCount = () => {
    notificationservice.getUnReadNotificationCount()
      .then((response) => {
        if (response) {
          setUnReadedNotificationCount(response);
        }
      });
  };

  const getUnReadedMessageCount = () => {
    chatService.getUnReadedMessageCount().then((response) => {
      if (response) {
        const unReadObj = {};
        response.forEach((r) => {
          unReadObj[r.threadId] = r.count;
        });
        setUnReadedMessageCount(unReadObj);
      }
    });
  };

  const fixThreadList = (message, increaseUnRead = false) => {
    const fixThreads = [...threads];
    const index = fixThreads.findIndex((f) => f.threadId === message.threadId);
    const oldThread = { ...fixThreads[index] };
    fixThreads.splice(index, 1);
    oldThread.lastMessage = { ...message };

    if (increaseUnRead) {
      if (!oldThread.unReadedCount) {
        oldThread.unReadedCount = 0;
      }
      oldThread.unReadedCount += 1;
    }

    fixThreads.unshift(oldThread);
    setThreads(fixThreads);
  };

  const sendMessage = (message) => {
    const model = {
      message,
      chatThreadId: activeThreadId
    };
    chatService.sendMessage(model).then((response) => {
      if (response) {
        const allThreads = { ...threadModel };
        const currentThread = allThreads[activeThreadId];
        currentThread.messages.push(response);
        setThreadModel(allThreads);
        fixThreadList(response);
      }
    });
  };

  const handleNotificationChange = (notify) => {
    const oldNotifications = [...notifications];
    const message = { ...notify };
    message.createdAt = formatDistanceToNowStrict(new Date(message.createDate), { locale: tr });
    message.statusType = message.status ? 'order_placed' : 'new_message';
    message.description = message.message;
    try {
      message.jsonData = JSON.parse(message.jsonData);
    } catch (error) {
      message.jsonData = {};
    }
    oldNotifications.unshift(message);
    setNotifications(oldNotifications);
    const cloneUnReaded = { ...unReadedNotificationCount };
    cloneUnReaded[message.userType?.toLowerCase()] += 1;
    cloneUnReaded.total += 1;
    setUnReadedNotificationCount(cloneUnReaded);
  };

  const handleMessageChange = (message) => {
    const allThreads = { ...threadModel };
    const allUnReadedMessage = { ...unReadedMessageCount };
    if (!allThreads[message.threadId]) {
      allThreads[message.threadId] = {};
    }
    if (!allThreads[message.threadId].messages) {
      allThreads[message.threadId].messages = [];
    }
    if (!allThreads[message.threadId].unReadedCount) {
      allThreads[message.threadId].unReadedCount = 0;
    }
    if (!allUnReadedMessage[message.threadId]) {
      allUnReadedMessage[message.threadId] = 0;
    }
    allThreads[message.threadId].messages.push(message);
    setThreadModel(allThreads);
    fixThreadList(message, true);
    allUnReadedMessage[message.threadId] += 1;
    setUnReadedMessageCount(allUnReadedMessage);
  };

  const handleReadedMessage = (messages) => {
    const allThreads = { ...threadModel };
    messages.forEach((m) => {
      if (!allThreads[m.threadId]) {
        allThreads[m.threadId] = {
          messages: []
        };
      }
      const messageIndex = allThreads[m.threadId].messages.findIndex((f) => f.id === m.id);
      if (messageIndex > -1) {
        allThreads[m.threadId].messages[messageIndex].isReaded = m.isReaded;
      }
    });
    setThreadModel(allThreads);
  };

  const handleNewChat = (message) => {
    const allThreads = [...threads];
    allThreads.unshift(message);
    setThreads(allThreads);
    const { fullName } = helpers.getUser();
    if (message.userName !== fullName) {
      const allUnReadedMessageCount = { ...unReadedMessageCount };
      allUnReadedMessageCount[message.threadId] = 1;
      setUnReadedMessageCount(allUnReadedMessageCount);
    }
  };

  const notifyWithAudio = () => {
    const audio = new Audio(`${window.location.origin}/static/audio/message_notification.mp3`);
    const promise = audio.play();
    if (promise !== undefined) {
      // eslint-disable-next-line no-unused-vars
      promise.then((_) => {
        // Autoplay started!
      }).catch(() => {
        audio.muted = true;
        audio.play();
      });
    }
  };

  const changeThreads = () => {
    if (threads.length === 0) {
      return;
    }
    const cloneThreads = [...threads];
    const currentThreadIndex = threads.findIndex((f) => f.threadId === activeThreadId);
    if (currentThreadIndex > -1) {
      cloneThreads[currentThreadIndex].unReadedCount = 0;
      setThreads(cloneThreads);
    }
  };

  const setReaded = () => {
    const model = {
      threadId: activeThreadId,
    };
    chatService.setReadedThread(model).then((response) => {
      if (response || response === '') {
        getUnReadedMessageCount();
        changeThreads();
      }
    });
  };

  const handleReadAllUnreadThread = () => {
    const model = {
      threadId: activeThreadId
    };

    chatService.setUnreadAllThread(model).then(() => {
      getUnReadedMessageCount();
    });
  };

  useEffect(() => {
    const connect = () => {
      const connection = new HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_BASE_URL_PURE}/hubs/chat?authToken=${helpers.getUser()?.token}`)
        .withAutomaticReconnect()
        .build();

      connection.onclose((result) => {
        console.log(`closed, ${result}`);
      });

      connection.start()
        .then(() => {
          connection.invoke('GetBalance');
          connection.invoke('CheckAnnouncement');
          connection.on('ReceiveMessage', (message) => {
            setNewMessage(message);
            notifyWithAudio();
          });

          connection.on('ReceiveNotification', (message) => {
            setNewNotification(message);
          });

          connection.on('NewChat', (message) => {
            setNewChat(message);
          });

          connection.on('MarkedAsReadMessages', (messages) => {
            setNewReadedMessage(messages);
          });

          connection.on('UpdateBalance', (message) => {
            setLastBalance(message);
          });

          connection.on('ShowAnnounce', (message) => {
            setAnnouncement(message);
          });
        })
        .catch((e) => console.log('Connection failed: ', e));
    };

    connect();
  }, []);

  useEffect(() => {
    if (newMessage) {
      handleMessageChange(newMessage);
    }
  }, [newMessage]);

  useEffect(() => {
    if (newNotification) {
      handleNotificationChange(newNotification);
    }
  }, [newNotification]);

  useEffect(() => {
    if (newReadedMessage && newReadedMessage.length > 0) {
      handleReadedMessage(newReadedMessage);
    }
  }, [newReadedMessage]);

  useEffect(() => {
    const allThreads = { ...threadModel };
    let currentThread = allThreads[activeThreadId];
    if (!currentThread) {
      currentThread = {};
    }
    currentThread.messages = [];
    setThreadModel(allThreads);

    if (activeThreadId) {
      setScrollCount(0);
      setIsFinish(false);
      getMessages();
      changeThreads();
      setReaded();
    }
  }, [activeThreadId]);

  useEffect(() => {
    if (newChat) {
      handleNewChat(newChat);
    }
  }, [newChat]);

  useEffect(() => {
    if (activeThreadId) {
      getMessages();
    }
  }, [scrollCount]);

  useEffect(() => {
    const getThreads = () => {
      chatService.getChatThreads().then((response) => {
        if (response && Array.isArray(response)) {
          setThreads(response);
        }
      });
    };

    getThreads();
    getUnReadedNotficationCount();
    getUnReadedMessageCount();
  }, []);

  return (
    <ChatContext.Provider
      value={{
        activeThreadId,
        setActiveThreadId,
        threads,
        scrollCount,
        setScrollCount,
        threadModel,
        setThreadModel,
        sendMessage,
        notifications,
        setNotifications,
        setIsLoading,
        isLoading,
        isFinish,
        setIsFinish,
        newMessage,
        getUnReadedNotficationCount,
        unReadedNotificationCount,
        unReadedMessageCount: Object.keys(unReadedMessageCount).length,
        getUnReadedMessageCount,
        setReaded,
        lastBalance,
        announcement,
        handleReadAllUnreadThread
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

ChatProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export { ChatContext, ChatProvider };
